import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, GitCompareArrows, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useCopyFlagCount } from '@/queries/copy';
import type { CopyFlagFilter } from '@/schemas/admin/copy';

/**
 * Sync fallout, surfaced where the work happens.
 *
 * The manifest sync (PHASE 02) marks keys `orphaned` when they leave the
 * manifest and `baseline_changed` when the shipped English moved under an
 * override. Both mean an override may now be serving something nobody intended,
 * and neither is visible from a normal catalog view — hence a banner rather
 * than a column.
 *
 * Dismissal is per-tab and per-count: acknowledging 3 orphans does not silence
 * the banner when a later sync produces a 4th.
 */

interface Flag {
  flag: CopyFlagFilter;
  Icon: React.ComponentType<{ className?: string }>;
  tone: 'warn' | 'info';
}

const FLAGS: readonly Flag[] = [
  { flag: 'orphaned', Icon: AlertTriangle, tone: 'warn' },
  { flag: 'baseline_changed', Icon: GitCompareArrows, tone: 'info' },
];

const TONE: Record<Flag['tone'], string> = {
  warn: 'border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)]',
  info: 'border-[color:var(--color-info)] bg-[color:var(--color-info-soft)]',
};

const ICON_TONE: Record<Flag['tone'], string> = {
  warn: 'text-[color:var(--color-warn)]',
  info: 'text-[color:var(--color-info)]',
};

export function SyncBanner({ className }: { className?: string }) {
  const { t } = useTranslation('copy');
  const [dismissed, setDismissed] = React.useState<Record<string, boolean>>({});

  const orphaned = useCopyFlagCount('orphaned');
  const baselineChanged = useCopyFlagCount('baseline_changed');
  const results = { orphaned, baseline_changed: baselineChanged } as const;

  const rows = FLAGS.map((f) => {
    const data = results[f.flag as 'orphaned' | 'baseline_changed'].data;
    const count = data?.count ?? 0;
    // `100+` when the server says there is another page — an honest ceiling
    // beats a number the catalog endpoint never promised.
    const label = data?.more ? `${count}+` : String(count);
    return { ...f, count, label, token: `${f.flag}:${label}` };
  }).filter((r) => r.count > 0 && !dismissed[r.token]);

  if (rows.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {rows.map(({ flag, Icon, tone, label, token }) => (
        <div
          key={flag}
          role="status"
          className={cn(
            'flex flex-wrap items-start gap-3 rounded-[var(--radius-md)] border p-3 text-sm',
            TONE[tone]
          )}
        >
          <Icon className={cn('mt-0.5 size-5 shrink-0', ICON_TONE[tone])} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[color:var(--color-fg)]">
              {t(`sync.${flag}.title`, { n: label })}
            </p>
            <p className="text-[color:var(--color-fg-muted)]">{t(`sync.${flag}.body`)}</p>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to="/copy"
              search={{ flag }}
              className="rounded-[var(--radius-md)] px-3 py-1.5 text-sm font-semibold text-[color:var(--color-accent)] underline underline-offset-2 hover:bg-[color:var(--color-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
            >
              {t(`sync.${flag}.action`)}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('sync.dismiss')}
              onClick={() => setDismissed((prev) => ({ ...prev, [token]: true }))}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
